export const introductionToSociologyCourse = {
  id: "introduction-to-sociology-2024",
  title: "Introduction to Sociology: Society, Culture & Social Dynamics",
  shortDescription: "Explore human society, social institutions, and the forces that shape our collective behavior and cultural patterns.",
  description: "Comprehensive introduction to sociological thinking and analysis. Students will examine social structures, institutions, and processes that shape human behavior in groups and societies. The course covers major sociological theories, research methods, and contemporary social issues including inequality, deviance, family, education, and social change.",
  category: "Social Sciences",
  industryCategory: "Social Sciences",
  difficulty: "beginner",
  duration: 15,
  estimatedWeeks: 15,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from sociology professors and social science researchers",
  thumbnailUrl: "https://images.unsplash.com/photo-1558618047-3c8c76ca7d13?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand major sociological theories and perspectives",
    "Analyze social structures and institutions",
    "Examine patterns of social inequality and stratification",
    "Evaluate the role of culture in shaping behavior",
    "Apply sociological research methods to social problems",
    "Analyze social change and its impact on communities",
    "Understand the relationship between individual and society",
    "Examine contemporary social issues through sociological lens"
  ],

  prerequisites: [
    "High school education",
    "Basic reading comprehension",
    "Interest in human behavior and society"
  ],

  skills: [
    "Sociological Analysis",
    "Research Methods",
    "Critical Thinking",
    "Social Theory",
    "Cultural Analysis",
    "Data Interpretation",
    "Social Issues Analysis",
    "Communication Skills"
  ],

  marketData: {
    averageSalary: 52000,
    salaryRange: {
      min: 35000,
      max: 75000
    },
    jobDemand: 3.8,
    marketTrend: "stable",
    careerPaths: [
      "Social Worker",
      "Research Analyst",
      "Community Organizer",
      "Human Resources Specialist",
      "Public Policy Analyst",
      "Non-profit Manager"
    ],
    relatedSkills: [
      "Research Methods",
      "Data Analysis",
      "Communication",
      "Critical Thinking",
      "Cultural Competency",
      "Social Research"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "introduction-to-sociology",
      title: "What is Sociology?",
      description: "Introduction to sociological thinking, perspectives, and the sociological imagination",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "sociology-intro-video",
          title: "Introduction to Sociology",
          description: "Overview of sociology as a social science and its unique perspective",
          type: "video",
          duration: 30
        },
        {
          id: "sociological-imagination",
          title: "The Sociological Imagination",
          description: "Understanding C. Wright Mills' concept of connecting personal troubles to public issues",
          type: "text",
          duration: 45,
          textContent: `# The Sociological Imagination

## Understanding Society Through a Sociological Lens

### What is Sociology?

Sociology is the systematic study of human society, social behavior, and social groups. Unlike psychology, which focuses on individual behavior, sociology examines how social forces shape our lives, opportunities, and choices.

**Key Characteristics of Sociology:**
- **Scientific Approach**: Uses systematic research methods
- **Social Focus**: Examines groups, institutions, and societies
- **Pattern Recognition**: Identifies social trends and regularities
- **Critical Analysis**: Questions taken-for-granted assumptions

### The Sociological Imagination

**C. Wright Mills' Revolutionary Concept**

C. Wright Mills introduced the concept of "sociological imagination" - the ability to connect personal experiences to larger social forces and historical context.

**Core Elements:**
- **Personal Troubles vs. Public Issues**: Understanding how individual problems reflect broader social patterns
- **Historical Context**: Recognizing how historical events shape current social conditions
- **Social Structure**: Seeing how social institutions influence individual opportunities
- **Global Perspective**: Understanding interconnections across societies

### Examples of Sociological Imagination

**Unemployment**
- *Personal Trouble*: An individual loses their job
- *Public Issue*: Economic recession affecting millions
- *Sociological Analysis*: Economic structures, globalization, technological change

**Education Achievement**
- *Personal Trouble*: A student struggles academically
- *Public Issue*: Educational inequality and achievement gaps
- *Sociological Analysis*: Social class, school funding, cultural capital

**Marriage and Divorce**
- *Personal Trouble*: A couple divorces
- *Public Issue*: Changing divorce rates and family structures
- *Sociological Analysis*: Economic pressures, gender roles, cultural values

### Major Sociological Perspectives

**Three Primary Theoretical Frameworks**

**1. Structural Functionalism**
- Society as an integrated system
- Focus on stability and order
- Institutions serve important functions
- Example: Education transmits knowledge and sorts students

**2. Conflict Theory**
- Society characterized by inequality and competition
- Focus on power struggles and exploitation
- Change through conflict resolution
- Example: Class struggle between workers and owners

**3. Symbolic Interactionism**
- Focus on face-to-face interactions
- Meaning created through social interaction
- Emphasis on symbols and interpretation
- Example: How we interpret gestures and language

### Why Study Sociology?

**Personal Benefits:**
- Develop critical thinking skills
- Understand your place in society
- Recognize social influences on behavior
- Become a more informed citizen

**Professional Applications:**
- Social work and human services
- Business and marketing
- Education and training
- Public policy and government
- Research and analysis

**Social Benefits:**
- Address social problems effectively
- Promote social justice and equality
- Build stronger communities
- Facilitate positive social change

The sociological imagination helps us understand that our individual lives are shaped by social forces beyond our control, while also recognizing our ability to influence and change society.`
        },
        {
          id: "sociology-exercise",
          title: "Applying Sociological Imagination",
          description: "Interactive exercise to practice connecting personal experiences to social forces",
          type: "social-sciences-interactive",
          duration: 25,
          interactiveData: {
            exerciseType: "sociological-analysis",
            instructions: "Analyze personal situations using the sociological imagination to identify broader social patterns and forces.",
            scenarios: [
              {
                id: "scenario1",
                description: "A college graduate moves back in with parents due to student debt",
                personalTrouble: "Individual financial struggle",
                publicIssue: "Rising education costs and student debt crisis",
                socialForces: ["Economic inequality", "Higher education policy", "Job market conditions"]
              },
              {
                id: "scenario2",
                description: "A working mother struggles to balance career and childcare",
                personalTrouble: "Work-life balance difficulty",
                publicIssue: "Lack of family-friendly workplace policies",
                socialForces: ["Gender roles", "Economic policy", "Cultural expectations"]
              }
            ],
            hints: [
              "Consider how historical and economic factors influence personal situations",
              "Think about social institutions and their impact",
              "Look for patterns that affect many people, not just individuals"
            ]
          }
        }
      ],
      quiz: {
        id: "sociology-intro-quiz",
        title: "Introduction to Sociology Quiz",
        description: "Test your understanding of basic sociological concepts",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the sociological imagination?",
            options: [
              "The ability to predict future social trends",
              "The connection between personal troubles and public issues",
              "The study of individual psychology",
              "The analysis of economic systems"
            ],
            correctAnswer: 1,
            explanation: "The sociological imagination, developed by C. Wright Mills, is the ability to connect personal experiences to larger social forces and historical context."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Which theoretical perspective focuses on social stability and order?",
            options: [
              "Conflict theory",
              "Symbolic interactionism",
              "Structural functionalism",
              "Feminist theory"
            ],
            correctAnswer: 2,
            explanation: "Structural functionalism views society as an integrated system where institutions work together to maintain stability and order."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What distinguishes sociology from psychology?",
            options: [
              "Sociology focuses on individual behavior",
              "Psychology studies social groups",
              "Sociology examines social forces and group behavior",
              "Psychology uses scientific methods"
            ],
            correctAnswer: 2,
            explanation: "Sociology focuses on social forces, group behavior, and social institutions, while psychology primarily studies individual behavior and mental processes."
          }
        ]
      }
    },

    {
      id: "sociological-theory",
      title: "Classical Sociological Theory",
      description: "Major theoretical perspectives from founding fathers of sociology",
      order: 2,
      estimatedHours: 4.0,
      content: [
        {
          id: "classical-theorists",
          title: "Founding Fathers of Sociology",
          description: "Durkheim, Marx, and Weber's contributions to sociological thought",
          type: "video",
          duration: 35
        },
        {
          id: "theoretical-perspectives",
          title: "Major Theoretical Frameworks",
          description: "Functionalism, conflict theory, and symbolic interactionism",
          type: "text",
          duration: 50,
          textContent: `# Major Theoretical Frameworks in Sociology\n\n## Understanding Social Theory\n\n### Structural Functionalism\n\n**Core Principles:**\n- Society as an integrated system of interrelated parts\n- Each part serves a function for the whole\n- Emphasis on social stability and order\n- Institutions work together to maintain equilibrium\n\n**Key Theorists:**\n- **Émile Durkheim**: Social solidarity and collective consciousness\n- **Talcott Parsons**: Social systems theory\n- **Robert Merton**: Manifest and latent functions\n\n**Applications:**\n- Education system preparing workers and citizens\n- Family providing socialization and emotional support\n- Religion creating social cohesion and moral guidance\n\n### Conflict Theory\n\n**Core Principles:**\n- Society characterized by competition for scarce resources\n- Power struggles between different groups\n- Focus on inequality, exploitation, and social change\n- Emphasis on how dominant groups maintain control\n\n**Key Theorists:**\n- **Karl Marx**: Class conflict and capitalism critique\n- **Max Weber**: Multiple sources of conflict (class, status, party)\n- **C. Wright Mills**: Power elite theory\n\n**Applications:**\n- Economic inequality between social classes\n- Gender discrimination in the workplace\n- Racial disparities in criminal justice system\n\n### Symbolic Interactionism\n\n**Core Principles:**\n- Focus on face-to-face interactions and meaning-making\n- Society constructed through symbolic communication\n- Emphasis on subjective interpretation of social reality\n- Micro-level analysis of social behavior\n\n**Key Theorists:**\n- **George Herbert Mead**: Self-development through interaction\n- **Herbert Blumer**: Symbolic interactionism framework\n- **Erving Goffman**: Dramaturgy and impression management\n\n**Applications:**\n- How students and teachers interact in classrooms\n- Development of personal identity through social roles\n- Non-verbal communication and social gestures\n\nThese three perspectives provide different lenses for understanding social phenomena and complement each other in sociological analysis.`
        },
        {
          id: "theory-application",
          title: "Applying Sociological Theories",
          description: "Practice analyzing social issues using different theoretical perspectives",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "theory-application",
            instructions: "Analyze the same social issue from functionalist, conflict, and symbolic interactionist perspectives.",
            scenarios: [
              {
                id: "education-inequality",
                issue: "Achievement gaps in public education",
                functionalist: "Schools sort students by ability for efficient workforce preparation",
                conflict: "Wealthy schools get better resources, perpetuating class inequality",
                symbolic: "Teacher expectations and labeling affect student self-concept and performance"
              }
            ]
          }
        }
      ],
      quiz: {
        id: "theory-quiz",
        title: "Sociological Theory Quiz",
        description: "Test your knowledge of classical sociological theories",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which theoretical perspective focuses primarily on face-to-face interactions?",
            options: ["Structural functionalism", "Conflict theory", "Symbolic interactionism", "Exchange theory"],
            correctAnswer: 2,
            explanation: "Symbolic interactionism focuses on micro-level interactions and how people create meaning through symbols and face-to-face communication."
          }
        ]
      }
    },

    {
      id: "culture-and-society",
      title: "Culture and Society",
      description: "Examining the role of culture in shaping human behavior and social organization",
      order: 3,
      estimatedHours: 4.0,
      content: [
        {
          id: "culture-concepts",
          title: "Understanding Culture",
          description: "Core concepts of culture, including values, norms, beliefs, and symbols",
          type: "video",
          duration: 35
        },
        {
          id: "cultural-analysis",
          title: "Elements of Culture",
          description: "Comprehensive analysis of cultural components and their social functions",
          type: "text",
          duration: 50,
          textContent: `# Elements of Culture

## Understanding How Culture Shapes Society

### Defining Culture

Culture encompasses the entire way of life of a society, including both material and non-material elements that are learned and shared by members of a group.

**Key Characteristics of Culture:**
- **Learned**: Acquired through socialization, not biological inheritance
- **Shared**: Common to members of a society or group
- **Symbolic**: Uses symbols to convey meaning
- **Adaptive**: Changes in response to environmental and social conditions
- **Integrated**: Various elements work together as a system

### Components of Culture

**Material Culture**
Physical objects created and used by society:
- **Technology**: Tools, machines, buildings
- **Art and Artifacts**: Paintings, sculptures, crafts
- **Consumer Goods**: Clothing, furniture, vehicles
- **Infrastructure**: Roads, bridges, communication systems

**Non-Material Culture**
Abstract elements that guide behavior:
- **Values**: Fundamental beliefs about what is good, right, and desirable
- **Norms**: Rules and expectations for behavior
- **Beliefs**: Ideas about the nature of reality
- **Language**: System of symbols for communication
- **Customs**: Traditional practices and rituals

### Values and Norms

**Values: Core Cultural Principles**

American core values include:
- **Individual Achievement**: Success through personal effort
- **Equality**: Equal opportunities for all
- **Freedom**: Personal liberty and choice
- **Democracy**: Participation in governance
- **Material Comfort**: Economic prosperity

**Norms: Behavioral Guidelines**

**Types of Norms:**
- **Folkways**: Everyday customs and etiquette
  - Example: Saying "please" and "thank you"
- **Mores**: Morally significant norms
  - Example: Prohibitions against stealing
- **Laws**: Formally codified norms
  - Example: Traffic regulations
- **Taboos**: Strongest prohibitions
  - Example: Incest taboos

### Cultural Diversity

**Subcultures**
Groups within society with distinct cultural patterns:
- **Ethnic Groups**: Shared heritage and traditions
- **Generational Groups**: Age-based cultural differences
- **Occupational Groups**: Work-related cultural patterns
- **Regional Groups**: Geographic cultural variations

**Countercultures**
Groups that reject mainstream cultural values:
- **Historical Examples**: 1960s hippie movement
- **Contemporary Examples**: Environmental activists, alternative lifestyle communities

### Language and Communication

**Language as Cultural Foundation**

**Functions of Language:**
- **Communication**: Sharing information and ideas
- **Cultural Transmission**: Passing culture to next generation
- **Social Integration**: Creating group solidarity
- **Social Control**: Maintaining behavioral standards

**Sapir-Whorf Hypothesis**
Language shapes thought and perception:
- **Strong Version**: Language determines thought
- **Weak Version**: Language influences thought
- **Examples**: Color perception, spatial concepts

### Cultural Change

**Sources of Cultural Change**

**Innovation**
- **Discovery**: Finding new knowledge
- **Invention**: Creating new cultural elements
- **Examples**: Internet, social media platforms

**Diffusion**
- **Cultural Borrowing**: Adopting elements from other cultures
- **Globalization**: Worldwide cultural exchange
- **Examples**: Food, music, fashion trends

**Cultural Integration and Lag**

**Cultural Integration**
How cultural elements work together:
- **Consistency**: Compatible values and practices
- **Adaptation**: Adjusting to new circumstances

**Cultural Lag**
When different parts of culture change at different rates:
- **Technology vs. Values**: Rapid technological change outpaces moral adaptation
- **Examples**: Social media privacy concerns, genetic engineering ethics

### Ethnocentrism and Cultural Relativism

**Ethnocentrism**
Judging other cultures by standards of one's own culture:
- **Characteristics**: Cultural superiority assumptions
- **Problems**: Prejudice, discrimination, conflict
- **Examples**: Colonialism, cultural imperialism

**Cultural Relativism**
Understanding cultures within their own context:
- **Principles**: No culture is inherently superior
- **Benefits**: Reduces prejudice, promotes understanding
- **Challenges**: Moral relativism concerns

### Socialization and Culture

**How Culture is Transmitted**

**Primary Socialization**
- **Family**: First and most important cultural transmission
- **Language Acquisition**: Learning cultural communication

**Secondary Socialization**
- **Schools**: Formal cultural education
- **Peers**: Age-group cultural sharing
- **Media**: Mass cultural transmission
- **Workplace**: Professional cultural learning

Understanding culture helps us recognize how deeply our thoughts, behaviors, and identities are shaped by the social groups we belong to, while also appreciating the rich diversity of human cultural expression.`
        },
        {
          id: "cultural-comparison",
          title: "Cross-Cultural Analysis Exercise",
          description: "Compare and contrast cultural practices across different societies",
          type: "social-sciences-interactive",
          duration: 30,
          interactiveData: {
            exerciseType: "cultural-comparison",
            instructions: "Analyze different cultural practices and apply concepts of cultural relativism vs. ethnocentrism.",
            scenarios: [
              {
                id: "marriage-customs",
                practice: "Arranged marriages vs. romantic marriages",
                cultures: ["Traditional Indian", "Contemporary American"],
                analysis: ["Family role", "Individual choice", "Social stability", "Economic factors"]
              },
              {
                id: "education-systems",
                practice: "Collective vs. individualistic learning approaches",
                cultures: ["Japanese group learning", "American individual achievement"],
                analysis: ["Social values", "Competition", "Cooperation", "Success measures"]
              }
            ],
            hints: [
              "Consider the function of practices within their cultural context",
              "Avoid judging by your own cultural standards",
              "Look for underlying values and beliefs that guide practices"
            ]
          }
        }
      ],
      quiz: {
        id: "culture-society-quiz",
        title: "Culture and Society Quiz",
        description: "Test your knowledge of cultural concepts and analysis",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the difference between folkways and mores?",
            options: [
              "Folkways are legal, mores are not",
              "Folkways are everyday customs, mores have moral significance",
              "Folkways are universal, mores are culture-specific",
              "Folkways are modern, mores are traditional"
            ],
            correctAnswer: 1,
            explanation: "Folkways are everyday customs and etiquette, while mores are norms with moral significance that are more strictly enforced."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "Cultural relativism means:",
            options: [
              "All cultures are exactly the same",
              "Understanding cultures within their own context",
              "Western culture is superior to others",
              "Culture doesn't influence behavior"
            ],
            correctAnswer: 1,
            explanation: "Cultural relativism involves understanding and evaluating cultures within their own context rather than judging them by outside standards."
          }
        ]
      }
    },

    {
      id: "socialization",
      title: "Socialization and the Life Course",
      description: "How individuals learn society's norms, values, and roles throughout their lives",
      order: 4,
      estimatedHours: 4.0,
      content: [
        {
          id: "socialization-process",
          title: "The Socialization Process",
          description: "How we learn to become members of society",
          type: "video",
          duration: 30
        },
        {
          id: "agents-of-socialization",
          title: "Agents of Socialization",
          description: "Family, school, peers, media, and other influences on social development",
          type: "text",
          duration: 45,
          textContent: `# Agents of Socialization\n\n## Primary Agents\n\n### Family\n- First and most influential agent\n- Teaches basic values, norms, and beliefs\n- Provides emotional foundation\n- Sets initial social class position\n\n### School\n- Formal education and knowledge transmission\n- Hidden curriculum of social rules\n- Peer interaction and competition\n- Authority relationships\n\n## Secondary Agents\n\n### Peer Groups\n- Age-based social learning\n- Independence from family values\n- Social acceptance and belonging\n- Youth culture transmission\n\n### Media and Technology\n- Mass communication influence\n- Role models and stereotypes\n- Global cultural exposure\n- Digital socialization\n\n### Workplace\n- Professional socialization\n- Adult role expectations\n- Economic relationships\n- Career identity formation\n\n### Religion\n- Moral and ethical guidance\n- Community belonging\n- Life meaning and purpose\n- Ritual and tradition\n\nEach agent plays a unique role in shaping our social identity and behavior throughout life.`
        }
      ],
      quiz: {
        id: "socialization-quiz",
        title: "Socialization Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which is typically considered the most influential agent of primary socialization?",
            options: ["School", "Family", "Peers", "Media"],
            correctAnswer: 1,
            explanation: "Family is typically the most influential agent of primary socialization, providing the first and most foundational learning experiences."
          }
        ]
      }
    },

    {
      id: "social-stratification",
      title: "Social Stratification and Inequality",
      description: "How societies organize people into hierarchies and the consequences of social inequality",
      order: 5,
      estimatedHours: 4.0,
      content: [
        {
          id: "stratification-systems",
          title: "Systems of Stratification",
          description: "Caste, class, and status systems around the world",
          type: "video",
          duration: 35
        },
        {
          id: "social-class",
          title: "Social Class in Modern Society",
          description: "Understanding class structure and social mobility",
          type: "text",
          duration: 50,
          textContent: `# Social Class in Modern Society\n\n## Class Structure\n\n### Upper Class\n- Top 1-3% of population\n- Significant wealth and power\n- Often inherited privilege\n- Control over major institutions\n\n### Middle Class\n- Professional and managerial occupations\n- Higher education credentials\n- Stable income and benefits\n- Homeownership and investments\n\n### Working Class\n- Manual and service occupations\n- Limited job security\n- Hourly wages\n- Limited benefits\n\n### Lower Class/Poor\n- Unemployment or low-wage work\n- Government assistance dependency\n- Limited educational opportunities\n- Health and housing challenges\n\n## Social Mobility\n\n### Types of Mobility\n- **Vertical**: Moving up or down class levels\n- **Horizontal**: Moving within same class level\n- **Intergenerational**: Between parent and child generations\n- **Intragenerational**: Within individual's lifetime\n\n### Factors Affecting Mobility\n- Education access and quality\n- Economic conditions\n- Social networks and connections\n- Geographic location\n- Discrimination and bias\n\nUnderstanding class helps explain life chances and opportunities in society.`
        }
      ],
      quiz: {
        id: "stratification-quiz",
        title: "Social Stratification Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is social mobility?",
            options: [
              "Movement between geographic locations",
              "Movement between social classes or positions",
              "Movement between different cultures",
              "Movement between age groups"
            ],
            correctAnswer: 1,
            explanation: "Social mobility refers to the movement of individuals or groups between different social classes or positions in the social hierarchy."
          }
        ]
      }
    },

    {
      id: "social-institutions",
      title: "Social Institutions",
      description: "Major institutions that organize society: family, education, religion, politics, and economy",
      order: 6,
      estimatedHours: 4.0,
      content: [
        {
          id: "family-institution",
          title: "The Family Institution",
          description: "Functions and forms of family in different societies",
          type: "video",
          duration: 30
        },
        {
          id: "institutional-analysis",
          title: "Analyzing Social Institutions",
          description: "How institutions work and change over time",
          type: "text",
          duration: 45,
          textContent: `# Social Institutions\n\n## Definition and Functions\n\nSocial institutions are organized systems of social relationships that meet basic societal needs.\n\n### Key Functions\n- **Organization**: Structure social life\n- **Socialization**: Teach norms and values\n- **Social Control**: Maintain order\n- **Meeting Needs**: Address basic human requirements\n\n## Major Institutions\n\n### Family\n- **Functions**: Reproduction, socialization, economic support, emotional care\n- **Forms**: Nuclear, extended, single-parent, blended families\n- **Changes**: Divorce rates, same-sex marriage, cohabitation\n\n### Education\n- **Functions**: Knowledge transmission, skill development, social sorting\n- **Levels**: Primary, secondary, higher education\n- **Issues**: Achievement gaps, funding disparities, standardized testing\n\n### Religion\n- **Functions**: Meaning-making, moral guidance, community building\n- **Types**: Organized religion, spirituality, secular worldviews\n- **Trends**: Secularization, religious diversity, fundamentalism\n\n### Politics and Government\n- **Functions**: Decision-making, law enforcement, resource allocation\n- **Systems**: Democracy, authoritarianism, totalitarianism\n- **Challenges**: Voter participation, political polarization, corruption\n\n### Economy\n- **Functions**: Production, distribution, consumption of goods and services\n- **Systems**: Capitalism, socialism, mixed economies\n- **Issues**: Unemployment, inequality, globalization\n\nInstitutions are interconnected and influence each other in complex ways.`
        }
      ],
      quiz: {
        id: "institutions-quiz",
        title: "Social Institutions Quiz",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "Which is NOT typically considered a major social institution?",
            options: ["Family", "Education", "Recreation", "Religion"],
            correctAnswer: 2,
            explanation: "Recreation, while important, is not typically considered one of the major social institutions. The major institutions are family, education, religion, politics, and economy."
          }
        ]
      }
    },

    {
      id: "deviance-and-social-control",
      title: "Deviance and Social Control",
      description: "Understanding rule-breaking behavior and society's responses to it",
      order: 7,
      estimatedHours: 4.0,
      content: [
        {
          id: "deviance-theories",
          title: "Theories of Deviance",
          description: "Why people violate social norms and expectations",
          type: "video",
          duration: 35
        },
        {
          id: "crime-and-punishment",
          title: "Crime and Social Control",
          description: "Criminal justice system and social responses to deviance",
          type: "text",
          duration: 45,
          textContent: `# Deviance and Social Control\n\n## Understanding Deviance\n\n### Definition\nDeviance is behavior that violates social norms and expectations.\n\n### Characteristics\n- **Relative**: What's deviant varies by time, place, and group\n- **Social Construction**: Society defines what's deviant\n- **Normative**: Based on social norms and values\n- **Reactive**: Depends on social response\n\n## Theories of Deviance\n\n### Strain Theory (Robert Merton)\n- **Cultural Goals vs. Institutional Means**\n- **Modes of Adaptation**:\n  - Conformity: Accept both goals and means\n  - Innovation: Accept goals, reject means (crime)\n  - Ritualism: Reject goals, accept means\n  - Retreatism: Reject both goals and means\n  - Rebellion: Replace both goals and means\n\n### Differential Association Theory (Edwin Sutherland)\n- Criminal behavior is learned through interaction\n- Learning includes techniques and attitudes\n- Exposure to favorable definitions of law violation\n\n### Labeling Theory\n- Deviance is socially constructed through labeling\n- Primary vs. secondary deviance\n- Self-fulfilling prophecy effects\n- Stigma and social exclusion\n\n## Social Control\n\n### Informal Social Control\n- Family, peers, community pressure\n- Social approval and disapproval\n- Gossip, shame, ostracism\n\n### Formal Social Control\n- Legal system and law enforcement\n- Courts, prisons, probation\n- Government regulations and sanctions\n\n## Crime and Criminal Justice\n\n### Types of Crime\n- **Violent Crime**: Assault, robbery, murder\n- **Property Crime**: Theft, burglary, vandalism\n- **White-Collar Crime**: Fraud, embezzlement, corruption\n- **Victimless Crime**: Drug use, prostitution, gambling\n\n### Criminal Justice System\n- **Law Enforcement**: Police investigation and arrest\n- **Courts**: Prosecution, defense, and adjudication\n- **Corrections**: Prison, probation, rehabilitation\n\nUnderstanding deviance helps explain how societies maintain order and handle rule-breaking.`
        }
      ],
      quiz: {
        id: "deviance-quiz",
        title: "Deviance and Social Control Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to strain theory, which mode of adaptation involves accepting cultural goals but rejecting legitimate means?",
            options: ["Conformity", "Innovation", "Ritualism", "Retreatism"],
            correctAnswer: 1,
            explanation: "Innovation involves accepting cultural goals (like wealth) but rejecting legitimate means to achieve them, often leading to criminal behavior."
          }
        ]
      }
    },

    {
      id: "social-change",
      title: "Social Change and Social Movements",
      description: "How societies transform over time and the role of collective action",
      order: 8,
      estimatedHours: 4.0,
      content: [
        {
          id: "sources-of-change",
          title: "Sources of Social Change",
          description: "Technology, demographics, culture, and other drivers of social transformation",
          type: "video",
          duration: 30
        },
        {
          id: "social-movements",
          title: "Social Movements and Collective Action",
          description: "How groups organize to create social change",
          type: "text",
          duration: 45,
          textContent: `# Social Change and Social Movements\n\n## Sources of Social Change\n\n### Technology\n- **Innovation**: New technologies transform society\n- **Diffusion**: Spread of technological innovations\n- **Examples**: Internet, automation, artificial intelligence\n\n### Demographics\n- **Population Growth**: Changes in size and composition\n- **Age Structure**: Aging populations, youth bulges\n- **Migration**: Movement of people and cultural mixing\n\n### Cultural Factors\n- **Values**: Changing beliefs and attitudes\n- **Ideologies**: New ways of thinking about society\n- **Globalization**: Cultural exchange and homogenization\n\n### Economic Factors\n- **Economic Systems**: Capitalism, socialism transitions\n- **Inequality**: Economic disparities and responses\n- **Labor Markets**: Changing work and employment\n\n## Social Movements\n\n### Definition\nOrganized efforts by groups to create or resist social change.\n\n### Types of Movements\n- **Reform Movements**: Gradual change within existing system\n- **Revolutionary Movements**: Fundamental system change\n- **Resistance Movements**: Opposition to unwanted change\n- **Expressive Movements**: Focus on personal transformation\n\n### Stages of Social Movements\n1. **Emergence**: Problem recognition and initial organization\n2. **Coalescence**: Formal organization and strategy development\n3. **Bureaucratization**: Institutional structure and procedures\n4. **Decline**: Success, failure, co-optation, or repression\n\n### Movement Resources\n- **Human Resources**: Members, leaders, supporters\n- **Material Resources**: Money, equipment, facilities\n- **Cultural Resources**: Symbols, narratives, knowledge\n- **Political Resources**: Access to decision-makers\n\n## Examples of Social Movements\n\n### Civil Rights Movement\n- Goal: Racial equality and civil rights\n- Tactics: Protests, boycotts, legal challenges\n- Outcomes: Civil Rights Act, Voting Rights Act\n\n### Women's Rights Movement\n- Goal: Gender equality and women's liberation\n- Tactics: Consciousness-raising, lobbying, demonstrations\n- Outcomes: Legal equality, workplace changes, reproductive rights\n\n### Environmental Movement\n- Goal: Environmental protection and sustainability\n- Tactics: Advocacy, direct action, policy reform\n- Outcomes: Environmental regulations, awareness raising\n\n## Globalization and Social Change\n\n### Economic Globalization\n- International trade and investment\n- Multinational corporations\n- Global labor markets\n\n### Cultural Globalization\n- Global media and communication\n- Cultural homogenization vs. diversity\n- Migration and cultural mixing\n\n### Political Globalization\n- International organizations\n- Global governance issues\n- Transnational social movements\n\nSocial change is constant, and understanding its sources and patterns helps us navigate and influence societal transformation.`
        }
      ],
      quiz: {
        id: "social-change-quiz",
        title: "Social Change Quiz",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What is the primary goal of reform movements?",
            options: [
              "Complete system overthrow",
              "Gradual change within existing system",
              "Personal transformation only",
              "Resistance to all change"
            ],
            correctAnswer: 1,
            explanation: "Reform movements seek gradual change within the existing social system rather than revolutionary transformation or complete overthrow."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "sociological-imagination-analysis",
      title: "Sociological Imagination Analysis Project",
      description: "Apply the sociological imagination to analyze a personal experience or contemporary social issue",
      type: "project",
      dueWeeks: 8,
      maxScore: 100,
      instructions: "Select a personal experience or contemporary social issue and analyze it using the sociological imagination. Connect individual experiences to broader social patterns and historical context.",
      submissionFormat: "pdf",
      resources: [
        "C. Wright Mills readings",
        "Contemporary social statistics",
        "Sociological research databases"
      ]
    }
  ],

  finalExam: {
    id: "sociology-final-exam",
    title: "Introduction to Sociology Final Examination",
    description: "Comprehensive examination covering all major sociological concepts, theories, and applications",
    timeLimit: 180,
    passingScore: 70,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "Which sociological perspective would most likely examine how educational institutions maintain social inequality?",
        options: [
          "Structural functionalism",
          "Conflict theory",
          "Symbolic interactionism",
          "Exchange theory"
        ],
        correctAnswer: 1,
        explanation: "Conflict theory focuses on how social institutions maintain and perpetuate inequality, making it the most appropriate perspective for examining educational inequality."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "The process by which individuals learn the culture of their society is called:",
        options: [
          "Acculturation",
          "Assimilation",
          "Socialization",
          "Enculturation"
        ],
        correctAnswer: 2,
        explanation: "Socialization is the lifelong process through which individuals learn the culture, values, norms, and behaviors of their society."
      }
    ]
  },

  quizzes: []
};